import * as React from 'react';
import { translate } from 'react-i18next';
import * as c from 'models/constants';
import { Field, reduxForm, getFormSyncErrors } from 'redux-form';
import { IStore } from 'redux/IStore';
import { IUserShippingAddress } from 'models/user/shipping/usershippingaddress';
import {
	IManageUserShippingAddress,
	IManageUserShippingAddressAction,
} from 'models/user/shipping/manageusershippingaddress';
import {
	getUserShippingAddress,
	submitCreateUserShippingAddress,
	submitUpdateUserShippingAddress,
} from 'modules/user/shipping/manageusershippingaddress';
import { InjectedRouter } from 'react-router';
import { IDomainValue, ICountryCallingCodeDomainValue } from 'models/common';
import { Button, ErrorSummary, LoadingOverlayWrapper } from 'components';
import { PortalPageSection } from 'components/PortalStructure';
import { PortalSelectMenu, PortalInputField } from 'components/FormControls';
import { required, maxLength, phoneNumber } from 'helpers/validationattributes';
import { browserHistory } from 'react-router';
import { getCountryCallingCodeDomainValues, getCountryDomainValues } from 'modules/domainvalues';
import { IDomainValuesAction } from 'models/domainvalues/domainvalues';
import { preventRouterLeave } from 'helpers/hooks';
import { ManageUserWrapper } from 'containers';
import { getUserProfile } from 'redux/modules/user/userprofile';
import { IUserProfile, IUserProfileAction } from 'models/user/userprofile';
const { connect } = require('react-redux');

interface IProps {
	manageUserShippingAddress?: IManageUserShippingAddress;
	currentFormValues: IUserShippingAddress;
	initialValues: IUserShippingAddress;
	location: any;
	router: InjectedRouter;
	route: any;
	countryDomainValues: IDomainValue[];
	countryCallingCodeDomainValues: ICountryCallingCodeDomainValue[];
	params: IParams;
	userValues: IUserProfile;

	/* form props */
	pristine: boolean;
	dirty: boolean;
	submitting: boolean;
	handleSubmit: any;
	submitFailed: boolean;
	formErrors?: any;

	/* functions props */
	t?: (key: string) => string;
	getCountryCallingCodeDomainValues?: () => IDomainValuesAction;
	getUserProfile: () => IUserProfileAction;
	getCountryDomainValues?: () => IDomainValuesAction;
	getUserShippingAddress?: (id: string) => IManageUserShippingAddressAction;
	submitCreateUserShippingAddress?: (shippingAddress: IUserShippingAddress) => IManageUserShippingAddressAction;
	submitUpdateUserShippingAddress?: (shippingAddress: IUserShippingAddress) => IManageUserShippingAddressAction;
}

interface IParams {
	id: string;
}

const maxLength25 = maxLength(25);
const maxLength50 = maxLength(50);
const maxLength255 = maxLength(255);

@preventRouterLeave()
@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class ManageUserShippingAddressForm extends React.Component<IProps, {}> {
	constructor(props) {
		super(props);

		this.onSubmit = this.onSubmit.bind(this);
	}

	public componentDidMount() {
		this.props.getUserProfile();
		if (
			!this.props.countryCallingCodeDomainValues ||
			(this.props.countryCallingCodeDomainValues && this.props.countryCallingCodeDomainValues.length === 0)
		) {
			this.props.getCountryCallingCodeDomainValues();
		}
		this.props.getCountryDomainValues();

		this.props.getUserShippingAddress(this.props.params.id);
	}

	public onSubmit(form: IUserShippingAddress) {
		if (this.props.manageUserShippingAddress.isEditMode) {
			this.props.submitUpdateUserShippingAddress(form);
		} else {
			this.props.submitCreateUserShippingAddress(form);
		}
	}

	private mapErrorKeyToMessage(key: string, t: (key: string) => string) {
		switch (key) {
			case 'name':
				return t('User_Shipping_ManageShippingAddress_Label_Name');

			case 'addressLine1':
				return t('User_Shipping_ManageShippingAddress_Label_AddressLine1');

			case 'addressLine2':
				return t('User_Shipping_ManageShippingAddress_Label_AddressLine2');

			case 'city':
				return t('User_Shipping_ManageShippingAddress_Label_City');

			case 'zipCode':
				return t('User_Shipping_ManageShippingAddress_Label_ZIPCode');

			case 'countryKey':
				return t('User_Shipping_ManageShippingAddress_Label_Country');

			case 'state':
				return t('User_Shipping_ManageShippingAddress_Label_State');

			case 'phoneNumber':
				return t('User_Shipping_ManageShippingAddress_Label_PhoneNumber');

			case 'note':
				return t('User_Shipping_ManageShippingAddress_Label_Note');

			default:
				return key;
		}
	}

	public render() {
		const {
			handleSubmit,
			t,
			formErrors,
			submitFailed,
			submitting,
			pristine,
			location,
			countryDomainValues,
			countryCallingCodeDomainValues,
			userValues,
		} = this.props;

		const errorSummaryProps = {
			formErrors,
			submitFailed,
			error: this.props.manageUserShippingAddress ? this.props.manageUserShippingAddress.error : false,
			message: this.props.manageUserShippingAddress ? this.props.manageUserShippingAddress.message : null,
			location,
			mapErrorKeyToMessage: this.mapErrorKeyToMessage,
		};

		let countries = [];
		if (countryDomainValues) {
			countries = countryDomainValues.map((country: IDomainValue) => {
				return {
					key: country.key,
					value: t(`DV_Countries_${country.key}`),
				};
			});

			countries.sort(function (a: IDomainValue, b: IDomainValue) {
				if (a.value < b.value) {
					return -1;
				}
				if (a.value > b.value) {
					return 1;
				}
				return 0;
			});
		}

		let countryCallingCodes = [];
		if (countryCallingCodeDomainValues) {
			countryCallingCodes = countryCallingCodeDomainValues.map((countryCallingCode: ICountryCallingCodeDomainValue) => {
				return {
					key: countryCallingCode.key,
					value: t(`DV_CountryCallingCodes_${countryCallingCode.key}`),
				};
			});
		}

		const cardTitle =
			this.props.manageUserShippingAddress && this.props.manageUserShippingAddress.isEditMode
				? t('User_Shipping_ManageShippingAddress_Label_EditAddress')
				: t('User_Shipping_ManageShippingAddress_Label_AddAddress');
		return (
			<ManageUserWrapper user={userValues}>
				<br />
				<div className="userShippingAddressForm">
					<LoadingOverlayWrapper
						active={this.props.manageUserShippingAddress.isFetching}
						spinner={true}
						text={t('BSPortal_Common_Loading')}
						background="rgba(0, 0, 0, 0.1)"
					>
						<div>
							<ErrorSummary {...errorSummaryProps} />
						</div>
						<form onSubmit={handleSubmit(this.onSubmit)}>
							<Field name="id" component="input" type="hidden" />
							<PortalPageSection
								title={cardTitle}
								variant="dark"
								readOnly={false}
								toggleEdit={() => {
									browserHistory.push('/user/edit/shippingaddresses');
								}}
							>
								<div className="row">
									<div className="col-md-12">
										<Field
											name="name"
											component={PortalInputField}
											type="text"
											label={t('User_Shipping_ManageShippingAddress_Label_Name')}
											validate={[maxLength50]}
										/>
									</div>
								</div>

								<div className="row">
									<div className="col-md-12">
										<Field
											name="addressLine1"
											component={PortalInputField}
											type="text"
											label={t('User_Shipping_ManageShippingAddress_Label_AddressLine1')}
											validate={[required, maxLength255]}
										/>
									</div>
								</div>

								<div className="row">
									<div className="col-md-12">
										<Field
											name="addressLine2"
											component={PortalInputField}
											type="text"
											label={t('User_Shipping_ManageShippingAddress_Label_AddressLine2')}
											validate={[maxLength255]}
										/>
									</div>
								</div>

								<div className="row">
									<div className="col-md-6">
										<Field
											name="city"
											component={PortalInputField}
											type="text"
											label={t('User_Shipping_ManageShippingAddress_Label_City')}
											validate={[required, maxLength255]}
										/>
									</div>
									<div className="col-md-6">
										<Field
											name="zipCode"
											component={PortalInputField}
											type="text"
											label={t('User_Shipping_ManageShippingAddress_Label_ZIPCode')}
											validate={[required, maxLength25]}
										/>
									</div>
								</div>

								<div className="row">
									<div className="col-md-6">
										<Field
											name="state"
											component={PortalInputField}
											type="text"
											label={t('User_Shipping_ManageShippingAddress_Label_State')}
											validate={[maxLength255]}
										/>
									</div>
									<div className="col-md-6">
										<Field
											name="countryKey"
											component={PortalSelectMenu}
											items={countries}
											isRequired={true}
											label={t('User_Shipping_ManageShippingAddress_Label_Country')}
										/>
									</div>
								</div>
								<div className="row">
									<div className="col-md-3">
										<Field
											name="callingCodeKey"
											component={PortalSelectMenu}
											items={countryCallingCodes}
											isRequired={true}
											label={t('BSPortal_Common_Phone_Prefix')}
										/>
									</div>
									<div className="col-md-9">
										<Field
											name="phoneNumber"
											component={PortalInputField}
											type="text"
											validate={[maxLength50, phoneNumber]}
											label={t('PhoneNumber_Label')}
										/>
									</div>
								</div>
								<div className="row">
									<div className="col-md-12">
										<Field
											name="note"
											component={PortalInputField}
											type="text"
											label={t('User_Shipping_ManageShippingAddress_Label_Note')}
											validate={[maxLength255]}
										/>
									</div>
								</div>
								<div className="row">
									<div className="col-md-12">
										<Button type="submit" disabled={pristine || submitting}>
											{t('BSPortal_Common_SaveChanges')}
										</Button>
									</div>
								</div>
							</PortalPageSection>
						</form>
					</LoadingOverlayWrapper>
				</div>
			</ManageUserWrapper>
		);
	}
}

let ManageUserShippingAddress = reduxForm({
	form: c.FORM_NAME_MANAGE_USER_SHIPPING_ADDRESS,
	enableReinitialize: true,
})(ManageUserShippingAddressForm);

ManageUserShippingAddress = connect(
	(state: IStore) => {
		let currentFormValues: IUserShippingAddress = null;
		if (
			state.form[c.FORM_NAME_MANAGE_USER_SHIPPING_ADDRESS] &&
			state.form[c.FORM_NAME_MANAGE_USER_SHIPPING_ADDRESS].values
		) {
			currentFormValues = state.form[c.FORM_NAME_MANAGE_USER_SHIPPING_ADDRESS].values;
		} else {
			currentFormValues = state.manageUserShippingAddress.shippingAddress;
		}

		return {
			userValues: state.userprofile.user,
			initialValues: state.manageUserShippingAddress.shippingAddress,
			manageUserShippingAddress: state.manageUserShippingAddress,
			currentFormValues,
			formErrors: getFormSyncErrors(c.FORM_NAME_MANAGE_USER_SHIPPING_ADDRESS)(state),
			countryDomainValues: state.domainValues.countries,
			countryCallingCodeDomainValues: state.domainValues.countryCallingCodes,
		};
	},
	(dispatch) => ({
		getCountryCallingCodeDomainValues: () => dispatch(getCountryCallingCodeDomainValues()),
		getCountryDomainValues: () => dispatch(getCountryDomainValues()),
		getUserShippingAddress: (id: string) => dispatch(getUserShippingAddress(id)),
		getUserProfile: () => dispatch(getUserProfile()),
		submitCreateUserShippingAddress: (shippingAddress: IUserShippingAddress) =>
			dispatch(submitCreateUserShippingAddress(shippingAddress)),
		submitUpdateUserShippingAddress: (shippingAddress: IUserShippingAddress) =>
			dispatch(submitUpdateUserShippingAddress(shippingAddress)),
	}),
)(ManageUserShippingAddress);

export { ManageUserShippingAddress };
